class WhatsAppService {
  constructor() {
    this.whatsappNumber = import.meta.env.VITE_WHATSAPP_NUMBER || '+22500000000';
    this.cachedNumber = null;
    this.lastFetch = 0;
    this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Récupérer le numéro WhatsApp depuis l'API
   */
  async fetchWhatsAppNumber() {
    const now = Date.now();
    
    // Utiliser le cache si disponible et récent
    if (this.cachedNumber && (now - this.lastFetch) < this.CACHE_DURATION) {
      return this.cachedNumber;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://api.baoubab.space'}/api/settings/public/whatsapp-number`);
      const data = await response.json();
      
      if (data.success && data.data.whatsapp_number) {
        this.cachedNumber = data.data.whatsapp_number;
        this.lastFetch = now;
        return this.cachedNumber;
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du numéro WhatsApp:', error);
    }

    // Fallback sur la variable d'environnement ou valeur par défaut
    return this.whatsappNumber;
  }

  /**
   * Obtenir le numéro WhatsApp (avec cache)
   */
  async getWhatsAppNumber() {
    return await this.fetchWhatsAppNumber();
  }

  /**
   * Nettoyer et formater le numéro de téléphone pour WhatsApp
   * @param {string} phoneNumber - Numéro de téléphone brut
   * @returns {string} Numéro nettoyé
   */
  cleanPhoneNumber(phoneNumber) {
    if (!phoneNumber) return '';
    
    // Supprimer tous les caractères non numériques sauf le +
    let cleaned = phoneNumber.replace(/[^\d+]/g, '');
    
    // Si le numéro commence par +, le garder, sinon l'ajouter si nécessaire
    if (!cleaned.startsWith('+')) {
      // Si le numéro commence par 00, remplacer par +
      if (cleaned.startsWith('00')) {
        cleaned = '+' + cleaned.substring(2);
      } else if (cleaned.length > 0) {
        // Ajouter le + au début si pas présent
        cleaned = '+' + cleaned;
      }
    }
    
    console.log('📞 Numéro nettoyé:', phoneNumber, '→', cleaned);
    return cleaned;
  }

  /**
   * Générer un message de commande formaté
   * @param {Object} product - Produit à commander
   * @param {number} quantity - Quantité désirée
   * @param {Object} user - Informations utilisateur
   * @returns {string} Message formaté
   */
  generateOrderMessage(product, quantity = 1, user = null) {
    // Debug: Afficher la structure du produit
    console.log('🔍 Debug WhatsApp - Produit reçu:', product);
    console.log('🔍 Debug WhatsApp - profilType:', product.profilType);
    console.log('🔍 Debug WhatsApp - agesSection:', product.agesSection);
    
    const totalPrice = (product.price * quantity).toFixed(2);
    const userName = user
      ? (user.name || `${user.firstname || ''} ${user.lastname || ''}`.trim() || user.email)
      : 'Utilisateur';
    
    const message = `*NOUVELLE COMMANDE BAOUBAB*

*Client:* ${user?.email || 'Non spécifié'}
 *Produit:* ${product.title}
 *Quantité:* ${quantity}
 *Prix unitaire:* ${product.price} FCFA
*Total:* ${totalPrice} FCFA

*Description:*
${product.description || 'Aucune description'}

 *Profil:* ${product.profilType?.name || product.profil_type?.name || 'Non spécifié'}
 *Âge:* ${product.agesSection?.name || product.ages_section?.name || 'Non spécifié'}

---
Merci de confirmer cette commande , les modalités de paiement et de livraison.`;

    return encodeURIComponent(message);
  }

  /**
   * Ouvrir WhatsApp avec le message pré-rempli
   * @param {Object} product - Produit à commander
   * @param {number} quantity - Quantité
   * @param {Object} user - Utilisateur
   */
  async openWhatsAppOrder(product, quantity = 1, user = null) {
    const rawNumber = await this.getWhatsAppNumber();
    const cleanedNumber = this.cleanPhoneNumber(rawNumber);
    
    // Générer le message brut (non encodé)
    const totalPrice = (product.price * quantity).toFixed(2);
    const userName = user
      ? (user.name || `${user.firstname || ''} ${user.lastname || ''}`.trim() || user.email)
      : 'Utilisateur';
    
    const rawMessage = `*NOUVELLE COMMANDE BAOUBAB*

*Client:* ${user?.email || 'Non spécifié'}
*Produit:* ${product.title}
*Quantité:* ${quantity}
*Prix unitaire:* ${product.price} FCFA
*Total:* ${totalPrice} FCFA

*Description:*
${product.description || 'Aucune description'}

*Profil:* ${product.profilType?.name || product.profil_type?.name || 'Non spécifié'}
*Âge:* ${product.agesSection?.name || product.ages_section?.name || 'Non spécifié'}

---
Merci de confirmer cette commande, les modalités de paiement et de livraison.`;

    // Détecter si on est sur mobile pour adapter l'encodage
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    let whatsappUrl;
    if (isMobile) {
      // Sur mobile, encodage simple pour préserver le formatage
      whatsappUrl = `https://wa.me/${cleanedNumber.replace('+', '')}?text=${encodeURIComponent(rawMessage)}`;
    } else {
      // Sur desktop, même encodage pour uniformité
      whatsappUrl = `https://wa.me/${cleanedNumber.replace('+', '')}?text=${encodeURIComponent(rawMessage)}`;
    }
    
    console.log('🔗 URL WhatsApp générée:', whatsappUrl);
    console.log('📞 Numéro utilisé:', rawNumber, '→', cleanedNumber);
    console.log('💬 Message original:', rawMessage);
    console.log('📱 Plateforme:', isMobile ? 'Mobile' : 'Desktop');
    
    if (isMobile) {
      // Sur mobile, utiliser directement window.location.href avec wa.me
      console.log('📱 Ouverture WhatsApp sur mobile avec wa.me');
      
      // Utiliser directement wa.me qui fonctionne mieux sur mobile
      window.location.href = whatsappUrl;
      
    } else {
      // Sur desktop, utiliser window.open
      console.log('💻 Ouverture WhatsApp sur desktop');
      
      // Essayer d'abord window.open, puis fallback sur location.href si bloqué
      const newWindow = window.open(whatsappUrl, '_blank');
      
      // Si le popup est bloqué, utiliser location.href
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        console.log('🔄 Popup bloqué, utilisation de location.href');
        window.location.href = whatsappUrl;
      }
    }
  }

  /**
   * Ouvrir WhatsApp avec un message libre (hors commande produit)
   * @param {string} rawMessage
   */
  async openWhatsAppMessage(rawMessage) {
    const rawNumber = await this.getWhatsAppNumber();
    const cleanedNumber = this.cleanPhoneNumber(rawNumber);
    const whatsappUrl = `https://wa.me/${cleanedNumber.replace('+', '')}?text=${encodeURIComponent(rawMessage)}`;

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile) {
      window.location.href = whatsappUrl;
      return;
    }

    const newWindow = window.open(whatsappUrl, '_blank');
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      window.location.href = whatsappUrl;
    }
  }

  /**
   * Ouvrir WhatsApp pour une demande d'accompagnement neuro
   * @param {Object} payload
   */
  async openWhatsAppNeuroSupport(payload = {}) {
    const {
      intent = 'accompagnement',
      user = null,
      kidFirstname = null,
      kidName = null,
      referenceCode = null,
      anonymousCode = null,
      profileTitle = null,
      dominantInsight = null,
      scores = null,
      spectra = null,
      axisLabels = null,
    } = payload;

    const ref = referenceCode || anonymousCode;
    const tutorName = user?.name || user?.email || null;
    const childLabel = [kidFirstname, kidName].filter(Boolean).join(' ') || null;

    const intro = intent === 'valed_neuro'
      ? "Bonjour, je souhaite constituer ma VALED NEURO après l'évaluation de mon enfant."
      : 'Bonjour, je souhaite être contacté pour un accompagnement suite au résultat neuro.';

    const lines = [
      '*DEMANDE ACCOMPAGNEMENT NEURO BAOUBAB*',
      '',
      intro,
      '',
      // tutorName ? `*Tuteur:* ${tutorName}` : null,
      // user?.email ? `*Email:* ${user.email}` : null,
      // user?.mobile_number ? `*Téléphone:* ${user.mobile_number}` : (user ? '*Téléphone:* —' : null),
      // childLabel ? `*Enfant:* ${childLabel}` : null,
      ref ? `*Référence test:* ${ref}` : null,
      profileTitle ? `*Profil affiché:* ${profileTitle}` : null,
      dominantInsight ? `*Élément dominant:* ${dominantInsight}` : null,
    ].filter(Boolean);

    if (scores && typeof scores === 'object') {
      lines.push('', '*Scores:*');
      const slugs = Array.isArray(spectra) && spectra.length
        ? spectra.map((s) => s.slug)
        : Object.keys(scores);
      slugs.forEach((slug) => {
        const label = axisLabels?.[slug] || spectra?.find((s) => s.slug === slug)?.label || slug;
        const max = spectra?.find((s) => s.slug === slug)?.max_score ?? 10;
        lines.push(`- ${label}: ${scores[slug] ?? 0}/${max}`);
      });
    }

    lines.push('', "Merci de me proposer la meilleure orientation d'accompagnement.");
    await this.openWhatsAppMessage(lines.join('\n'));
  }

  /**
   * Vérifier si WhatsApp est disponible sur l'appareil
   * @returns {boolean}
   */
  isWhatsAppAvailable() {
    // Détection basique du type d'appareil
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    return isMobile || window.navigator.userAgent.includes('WhatsApp');
  }

  /**
   * Générer l'URL WhatsApp (pour prévisualisation)
   * @param {Object} product - Produit
   * @param {number} quantity - Quantité
   * @param {Object} user - Utilisateur
   * @returns {string} URL WhatsApp
   */
  async getWhatsAppUrl(product, quantity = 1, user = null) {
    const rawNumber = await this.getWhatsAppNumber();
    const cleanedNumber = this.cleanPhoneNumber(rawNumber);
    
    // Générer le message brut (non encodé)
    const totalPrice = (product.price * quantity).toFixed(2);
    
    const rawMessage = `*NOUVELLE COMMANDE BAOUBAB*

*Client:* ${user?.email || 'Non spécifié'}
*Produit:* ${product.title}
*Quantité:* ${quantity}
*Prix unitaire:* ${product.price} FCFA
*Total:* ${totalPrice} FCFA

*Description:*
${product.description || 'Aucune description'}

*Profil:* ${product.profilType?.name || product.profil_type?.name || 'Non spécifié'}
*Âge:* ${product.agesSection?.name || product.ages_section?.name || 'Non spécifié'}

---
Merci de confirmer cette commande, les modalités de paiement et de livraison.`;

    return `https://wa.me/${cleanedNumber.replace('+', '')}?text=${encodeURIComponent(rawMessage)}`;
  }
}

export default new WhatsAppService();
